import React, { useState } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

import usePlacesAutocomplete, {
  GeocodeResult,
  getGeocode,
} from 'use-places-autocomplete';

type GooglePlacesSearchProps = {
  onSelectionMade(addressObj: any, geoCode: GeocodeResult): void;
};

const GooglePlacesSearch: React.FC<GooglePlacesSearchProps> = ({
  onSelectionMade,
}) => {
  const { suggestions, setValue, clearSuggestions } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'au' },
    },
    debounce: 300,
  });

  const [loading, setIsLoading] = useState(false);

  const formatGeocodeProps = ({ address_components }: GeocodeResult) => {
    return address_components.reduce((acc: any, addressComponent) => {
      const { short_name: value, types } = addressComponent;
      const newKeyValue: any = {};

      if (types.includes('route')) {
        newKeyValue.address_line_1 = `${
          acc.address_line_1 || ''
        } ${value}`.trim();
      }

      if (types.includes('street_number')) {
        newKeyValue.address_line_1 = `${value} ${
          acc.address_line_1 || ''
        }`.trim();
      }

      if (types.includes('locality')) {
        newKeyValue.suburb = value;
      }

      if (types.includes('administrative_area_level_1')) {
        newKeyValue.state = value;
      }

      if (types.includes('country')) {
        newKeyValue.country_code = value;
      }

      if (types.includes('postal_code')) {
        newKeyValue.post_code = value;
      }

      return {
        ...acc,
        ...newKeyValue,
      };
    }, {});
  };

  const handleInput = (queryString: string): void => {
    setIsLoading(true);
    setValue(queryString);
  };

  const handleSelect = async (suggestion: any) => {
    if (!suggestion[0]) return;
    setValue(suggestion[0].description, false);
    clearSuggestions();

    const parameter = {
      address: suggestion[0].description,
    };
    let geocode;
    try {
      [geocode] = await getGeocode(parameter);
      onSelectionMade(formatGeocodeProps(geocode), geocode);
    } catch (err) {
      console.error('[GOOGLE PLACES]:', err);
    }
  };

  return (
    <AsyncTypeahead
      searchText="searching..."
      id="search_for_address"
      emptyLabel=""
      isLoading={loading}
      labelKey="description"
      minLength={3}
      onSearch={query => handleInput(query)}
      onChange={selected => handleSelect(selected)}
      options={suggestions.data}
      placeholder="Search For Address"
    />
  );
};

export default GooglePlacesSearch;
